import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { NgSelectComponent } from '@ng-select/ng-select';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, combineLatest, firstValueFrom, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import {
  RouteTabGroupComponent,
  TabGroupConfig,
} from '@features/tab-group/route-tab-group.component';

import { IconComponent } from '@shared/components/icon/icon.component';
import { InputComponent } from '@shared/components/input/input.component';
import { MultiSelectDropdownComponent } from '@shared/components/multi-select-dropdown/multi-select-dropdown.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { FormErrorDirective } from '@shared/directives/form-error.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import {
  BudgetType,
  EventType,
  listTrialsQuery,
  PermissionType,
  TrialImplementationStatus,
  TrialPhase,
} from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { AuthService } from '@shared/store/auth/auth.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { MainStore } from '@shared/store/main/main.store';
import { OrganizationService } from '@shared/store/organization/organization.service';
import { TrialUserService } from '@shared/store/trial-users/trial-user.service';
import { TrialsQuery } from '@shared/store/trials/trials.query';
import { TrialsService } from '@shared/store/trials/trials.service';
import { TrialModel } from '@shared/store/trials/trials.store';
import { WorkflowService } from '@shared/store/workflow/workflow.service';
import { Option } from '@shared/types/components.type';
import { Utils } from '@shared/utils/utils';

import { SettingsService } from './settings.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    TooltipDirective,
    IconComponent,
    NgTemplateOutlet,
    NgSelectComponent,
    FormsModule,
    FormErrorDirective,
    MultiSelectDropdownComponent,
    ReactiveFormsModule,
    InputComponent,
    RouteTabGroupComponent,
    RouterOutlet,
  ],
  selector: 'aux-settings',
  templateUrl: './settings.component.html',
})
export class SettingsComponent implements OnDestroy {
  destroyRef = inject(DestroyRef);
  service = inject(SettingsService);
  trialsQuery = inject(TrialsQuery);
  workflowService = inject(WorkflowService);

  private authQuery = inject(AuthQuery);
  private authService = inject(AuthService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private eventService = inject(EventService);
  private formBuilder = inject(UntypedFormBuilder);
  private launchDarklyService = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private mainStore = inject(MainStore);
  private overlayService = inject(OverlayService);
  private route = inject(ActivatedRoute);
  private trialsService = inject(TrialsService);
  private trialUserService = inject(TrialUserService);
  private vendorsService = inject(OrganizationService);

  hasChanges = signal<boolean>(false);
  requiredAreasEmpty = signal<boolean>(false);

  saveButtonDisabled = computed(() => {
    return !this.hasChanges() || this.requiredAreasEmpty();
  });
  saveButtonTooltip = computed(() => {
    if (!this.hasChanges()) {
      return 'No changes to save.';
    }

    if (this.hasChanges() && this.requiredAreasEmpty()) {
      return 'Fill out required fields to save.';
    }

    return '';
  });

  saveBtn = viewChild<ElementRef<HTMLButtonElement>>('saveButton');

  currentTrial: listTrialsQuery | undefined;
  editButtonDisabledTooltip = MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
  editMode = new BehaviorSubject(false);
  fg = this.formBuilder.group({
    auxilius_start_date: null,
    implementation_status: [null, this.implementationStatusValidator()],
    indication: ['', Validators.required],
    program: null,
    project: null,
    short_name: ['', Validators.required],
    therapy_area: null,
    trial_phase: null,
    workspace_type: [null, Validators.required],
  });
  hasTrialSettingsPermission$ = new BehaviorSubject<boolean>(false);
  implementationStatusOptions = Utils.TRIAL_IMPLEMENTATION_STATUS_OPTIONS;
  initialFgValue = this.fg.value;
  tabs: TabGroupConfig[] = [
    { label: 'Users', route: ROUTING_PATH.SETTINGS.USERS },
    {
      label: 'User Permissions',
      route: ROUTING_PATH.SETTINGS.USER_PERMISSIONS,
      show: toObservable(this.launchDarklyService.$select((flags) => flags.tab_user_permissions)),
    },
    {
      label: 'Integrations',
      route: ROUTING_PATH.SETTINGS.INTEGRATIONS,
      show: toObservable(this.launchDarklyService.$select((flags) => flags.tab_integrations)),
    },
    {
      label: 'Exchange Rates',
      route: ROUTING_PATH.SETTINGS.EXCHANGE_RATES,
      show: toObservable(this.launchDarklyService.$select((flags) => flags.tab_exchange_rates)),
    },
    {
      label: 'Close Settings',
      route: ROUTING_PATH.SETTINGS.CLOSE_SETTINGS.INDEX,
      show: toObservable(
        this.launchDarklyService.$select(
          (flags) =>
            flags.close_settings &&
            (flags.expense_defaults ||
              flags.settings_journal_entry_configuration ||
              flags.balance_sheet_accounts)
        )
      ),
    },
    {
      label: 'Investigator Settings',
      route: ROUTING_PATH.SETTINGS.INVESTIGATOR_SETTINGS,
      show: toObservable(
        this.launchDarklyService.$select((flags) => flags.tab_investigator_settings)
      ),
    },
    {
      label: 'Study Specifications',
      route: ROUTING_PATH.SETTINGS.STUDY_SPECIFICATIONS,
      show: toObservable(
        this.launchDarklyService.$select((flags) => flags.tab_forecast_studyspecifications)
      ),
    },
  ];
  therapyAreaOptions: Option[] = [];
  trialPhaseOptions: Option<'TRIAL_PHASE_PHASE_1_2' | TrialPhase>[] = [
    {
      label: 'Pre Clinical',
      value: TrialPhase.TRIAL_PHASE_PRE_CLINICAL,
    },
    {
      label: 'Phase 1',
      value: TrialPhase.TRIAL_PHASE_PHASE_1,
    },
    {
      label: 'Phase 1/2',
      value: 'TRIAL_PHASE_PHASE_1_2',
    },
    {
      label: 'Phase 2',
      value: TrialPhase.TRIAL_PHASE_PHASE_2,
    },
    {
      label: 'Phase 3',
      value: TrialPhase.TRIAL_PHASE_PHASE_3,
    },
    {
      label: 'Phase 4',
      value: TrialPhase.TRIAL_PHASE_PHASE_4,
    },
  ];
  trialShortNameValidators: ValidatorFn[] = [
    (control) => {
      const value: string = control.value;

      if (!value) {
        return { required: true };
      }

      const exists = !!this.trialsQuery
        .getAll()
        .find((trial) => trial.short_name?.toLowerCase() === value.toLowerCase());

      return exists && this.currentTrial?.short_name !== value ? { duplicateTrial: true } : null;
    },
  ];
  workspaceEnum = Utils.TRIAL_WORKSPACE_TYPE_OPTIONS;
  workspaceTypeOptions = Object.entries(Utils.TRIAL_WORKSPACE_TYPE_OPTIONS).map(([key, label]) => ({
    key,
    label,
  }));

  ngOnDestroy() {
    this.mainStore.update({ fullPage: false });
  }

  async canDeactivate(): Promise<boolean> {
    if (this.fg.touched) {
      const result = this.overlayService.openUnsavedChangesConfirmation();
      const event = await firstValueFrom(result.afterClosed$);

      return !!event.data;
    }

    return true;
  }

  checkChangesFromForm() {
    if (this.fg.pristine) {
      this.initialFgValue = this.fg.value;
    }

    this.hasChanges.set(
      !Object.keys(this.initialFgValue).every((key) => {
        const fgValue = this.fg.value[key] ? this.fg.value[key] : null;
        const initialValue = this.initialFgValue[key] ? this.initialFgValue[key] : null;

        return fgValue === initialValue;
      })
    );

    const requiredAreas = [
      'short_name',
      'indication',
      'implementation_status',
      'workspace_type',
      'therapy_area',
      'trial_phase',
    ];

    this.requiredAreasEmpty.set(
      requiredAreas.some((key) => {
        return (
          !this.fg.value[key] ||
          this.fg.value[key].length === 0 ||
          this.fg.value[key] === TrialImplementationStatus.IMPLEMENTATION_STATUS_BLANK
        );
      })
    );
  }

  constructor() {
    this.trialUserService.get().pipe(takeUntilDestroyed()).subscribe();
    this.workflowService.getWorkflowList().pipe(takeUntilDestroyed()).subscribe();

    this.hasPermission();

    this.vendorsService
      .getListWithTotalBudgetAmount(BudgetType.BUDGET_PRIMARY)
      .pipe(takeUntilDestroyed())
      .subscribe();

    this.mainStore.update({ fullPage: true });

    combineLatest([
      this.eventService.select$(EventType.TRIAL_CHANGED),
      this.mainQuery.select('trialKey'),
    ])
      .pipe(
        takeUntilDestroyed(),
        switchMap(([{ trial_id }, trialKey]) => {
          const trialId = trialKey ? trialKey : trial_id;

          return combineLatest([this.authQuery.isLoggedIn$, of({ trial_id: trialId })]);
        }),
        switchMap((arr) => {
          return combineLatest([of(arr), this.trialsService.get()]);
        })
      )
      .subscribe(([[, { trial_id }], { data }]) => {
        if (data?.length) {
          this.setCurrentTrial(data.find((trial) => trial.id === trial_id) || data[0]);
        }
      });

    this.fg.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.checkChangesFromForm();
    });

    this.fg.controls['therapy_area'].valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      if (value && value.length === 0) {
        this.fg.controls['therapy_area'].markAsTouched();
      }
    });

    this.authQuery.isLoggedIn$
      .pipe(
        takeUntilDestroyed(),
        switchMap((x: boolean) => {
          if (x) {
            return this.trialsService.get().pipe(
              tap(({ data }) => {
                if (data?.length) {
                  const { queryParams } = this.route.snapshot;
                  let trialKey = queryParams.trial;

                  if (
                    queryParams.trial &&
                    data.filter((d) => d.id === queryParams.trial).length !== 0
                  ) {
                    trialKey = queryParams.trial;
                  } else {
                    trialKey = this.authQuery.getValue().trial_id;
                  }

                  this.setCurrentTrial(data.find((trial) => trial.id === trialKey) || data[0]);
                }
              })
            );
          }

          return of([]);
        })
      )
      .subscribe();

    void this.initTherapyAreaOptions();
  }

  editModeCancel() {
    this.fg.reset();
    this.patchFormValue();
    this.editMode.next(false);
  }

  getTrialPhaseValue(): string | TrialPhase | undefined {
    return isEqual(this.currentTrial?.trial_phase, [
      TrialPhase.TRIAL_PHASE_PHASE_1,
      TrialPhase.TRIAL_PHASE_PHASE_2,
    ])
      ? 'TRIAL_PHASE_PHASE_1_2'
      : this.currentTrial?.trial_phase[0];
  }

  hasPermission() {
    this.authService
      .isAuthorized$({
        permissions: [PermissionType.PERMISSION_UPDATE_TRIAL_SETTINGS],
        sysAdminsOnly: false,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((perm) => this.hasTrialSettingsPermission$.next(perm));
  }

  implementationStatusValidator(): ValidatorFn {
    return (control: AbstractControl): null | { [key: string]: unknown } => {
      const forbidden = control.value === TrialImplementationStatus.IMPLEMENTATION_STATUS_BLANK;

      return forbidden ? { blankStatus: { value: control.value } } : null;
    };
  }

  async initTherapyAreaOptions(): Promise<void> {
    this.therapyAreaOptions = await this.trialsService.getTherapyAreaOptions();
    this.changeDetectorRef.detectChanges();
  }

  onEditMode() {
    this.editMode.next(true);
  }

  async onSaveEdits() {
    const {
      auxilius_start_date,
      implementation_status,
      indication,
      program,
      project,
      short_name,
      therapy_area,
      trial_phase,
      workspace_type,
    } = this.fg.value;

    if (this.fg.valid) {
      if (this.currentTrial) {
        const trial = {
          auxilius_start_date,
          implementation_status,
          indication,
          program,
          project,
          short_name,
          therapy_area,
          trial_phase,
          workspace_type,
        } as Partial<TrialModel>;
        const success = await this.trialsService.update(this.currentTrial?.id, {
          ...trial,
          trial_phase:
            trial_phase === 'TRIAL_PHASE_PHASE_1_2'
              ? [TrialPhase.TRIAL_PHASE_PHASE_1, TrialPhase.TRIAL_PHASE_PHASE_2]
              : [trial_phase],
        });

        if (success) {
          this.currentTrial = {
            ...this.currentTrial,
            auxilius_start_date,
            implementation_status,
            indication,
            program,
            project,
            short_name,
            therapy_area,
            trial_phase:
              trial_phase === 'TRIAL_PHASE_PHASE_1_2'
                ? [TrialPhase.TRIAL_PHASE_PHASE_1, TrialPhase.TRIAL_PHASE_PHASE_2]
                : [trial_phase],
            workspace_type,
          };
        }

        this.editMode.next(false);
        this.fg.markAsUntouched();
        this.fg.markAsPristine();
        this.checkChangesFromForm();
      }
    }
  }

  patchFormValue(): void {
    this.fg.patchValue({
      ...this.currentTrial,
      trial_phase: isEqual(this.currentTrial?.trial_phase, [
        TrialPhase.TRIAL_PHASE_PHASE_1,
        TrialPhase.TRIAL_PHASE_PHASE_2,
      ])
        ? 'TRIAL_PHASE_PHASE_1_2'
        : this.currentTrial?.trial_phase[0],
    });
    this.fg.markAsUntouched();
    this.fg.markAsPristine();
  }

  setCurrentTrial(currTrial: listTrialsQuery) {
    this.currentTrial = {
      ...currTrial,
      nct_id:
        currTrial.nct_id === null || currTrial.nct_id === undefined
          ? currTrial.name
          : currTrial.nct_id,
    };
    this.patchFormValue();
    this.editMode.next(false);
  }

  triggerSave(): void {
    this.saveBtn()?.nativeElement.click();
  }
}
